import { axiosInstance } from './api';
import { buildFormData } from './mediaService';

/**
 * Chapters API Service
 * Handles all API calls for the My Life Chapters feature
 */

// =====================================================
// Chapters
// =====================================================

/**
 * Get all chapters for the current user
 */
export async function getChapters() {
  const response = await axiosInstance.get('/chapters/');
  return response.data;
}

/**
 * Get a single chapter with full details (milestones, memories)
 */
export async function getChapter(chapterId) {
  const response = await axiosInstance.get(`/chapters/${chapterId}/`);
  return response.data;
}

/**
 * Get chapter timeline data (organized by year)
 */
export async function getChapterTimeline(chapterId) {
  const response = await axiosInstance.get(`/chapters/${chapterId}/timeline/`);
  return response.data;
}

/**
 * Generate initial 5 chapters from birth year
 */
export async function generateChapters(birthYear, hometown = '') {
  const response = await axiosInstance.post('/chapters/generate/', {
    birth_year: birthYear,
    hometown,
  });
  return response.data;
}

/**
 * Update a chapter (rename, adjust dates)
 */
export async function updateChapter(chapterId, data) {
  const response = await axiosInstance.patch(`/chapters/${chapterId}/`, data);
  return response.data;
}

/**
 * Delete a chapter (only if empty)
 */
export async function deleteChapter(chapterId) {
  const response = await axiosInstance.delete(`/chapters/${chapterId}/`);
  return response.data;
}

/**
 * Create a custom chapter
 */
export async function createChapter(data) {
  const response = await axiosInstance.post('/chapters/', data);
  return response.data;
}

/**
 * Get all My Life data (profile, chapters, stats)
 */
export async function getMyLifeData() {
  const response = await axiosInstance.get('/chapters/my_life_data/');
  return response.data;
}

/**
 * Get My Life data for a specific person (guardian viewing child's data)
 */
export async function getMyLifeDataForPerson(personId) {
  const response = await axiosInstance.get(`/chapters/my_life_data/?person_id=${personId}`);
  return response.data;
}

// =====================================================
// Milestones
// =====================================================

/**
 * Get milestones (optionally filter by chapter)
 */
export async function getMilestones(chapterId = null) {
  const params = chapterId ? { chapter: chapterId } : {};
  const response = await axiosInstance.get('/chapter-milestones/', { params });
  return response.data;
}

/**
 * Get a single milestone
 */
export async function getMilestone(milestoneId) {
  const response = await axiosInstance.get(`/chapter-milestones/${milestoneId}/`);
  return response.data;
}

/**
 * Create a milestone
 */
export async function createMilestone(data) {
  const response = await axiosInstance.post('/chapter-milestones/', data);
  return response.data;
}

/**
 * Update a milestone
 */
export async function updateMilestone(milestoneId, data) {
  const response = await axiosInstance.patch(`/chapter-milestones/${milestoneId}/`, data);
  return response.data;
}

/**
 * Delete a milestone
 */
export async function deleteMilestone(milestoneId) {
  const response = await axiosInstance.delete(`/chapter-milestones/${milestoneId}/`);
  return response.data;
}

// =====================================================
// Memories
// =====================================================

/**
 * Get memories (optionally filter by chapter)
 */
export async function getMemories(chapterId = null) {
  const params = chapterId ? { chapter: chapterId } : {};
  const response = await axiosInstance.get('/chapter-memories/', { params });
  return response.data;
}

/**
 * Get a single memory
 */
export async function getMemory(memoryId) {
  const response = await axiosInstance.get(`/chapter-memories/${memoryId}/`);
  return response.data;
}

/**
 * Create a memory
 */
export async function createMemory(data) {
  const response = await axiosInstance.post('/chapter-memories/', data);
  return response.data;
}

/**
 * Update a memory
 */
export async function updateMemory(memoryId, data) {
  const response = await axiosInstance.patch(`/chapter-memories/${memoryId}/`, data);
  return response.data;
}

/**
 * Delete a memory
 */
export async function deleteMemory(memoryId) {
  const response = await axiosInstance.delete(`/chapter-memories/${memoryId}/`);
  return response.data;
}

// =====================================================
// Family Questions
// =====================================================

/**
 * Get family questions (optionally filter by status)
 */
export async function getChapterQuestions(status = null) {
  const params = status ? { status } : {};
  const response = await axiosInstance.get('/chapter-questions/', { params });
  return response.data;
}

/**
 * Answer a family question
 */
export async function answerQuestion(questionId, data) {
  const response = await axiosInstance.post(
    `/chapter-questions/${questionId}/answer/`,
    data
  );
  return response.data;
}

// =====================================================
// Reactions
// =====================================================

/**
 * Add a reaction to a milestone or memory
 */
export async function addReaction(data) {
  const response = await axiosInstance.post('/chapter-reactions/', data);
  return response.data;
}

/**
 * Remove a reaction
 */
export async function removeReaction(reactionId) {
  const response = await axiosInstance.delete(`/chapter-reactions/${reactionId}/`);
  return response.data;
}

// =====================================================
// Daily Question of the Day
// =====================================================

/**
 * Get today's daily question and user status
 */
export async function getDailyQuestion() {
  const response = await axiosInstance.get('/daily-question/today/');
  return response.data;
}

/**
 * Answer a daily question (creates a LifeEvent + Memory)
 * Supports optional media files via FormData.
 *
 * @param {string} questionId
 * @param {Object} data        - { text, title?, year? }
 * @param {Array}  mediaFiles  - Optional array of { file, name } for recorded media
 */
export async function answerDailyQuestion(questionId, data, mediaFiles = []) {
  if (mediaFiles.length > 0) {
    const formData = buildFormData(data, mediaFiles);
    const response = await axiosInstance.post(
      `/daily-question/${questionId}/answer/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }
  const response = await axiosInstance.post(`/daily-question/${questionId}/answer/`, data);
  return response.data;
}

/**
 * Skip a daily question
 */
export async function skipDailyQuestion(questionId) {
  const response = await axiosInstance.post(`/daily-question/${questionId}/skip/`);
  return response.data;
}

// =====================================================
// Utility: Quick Pick Types
// =====================================================

export const QUICK_PICK_TYPES = [
  { type: 'graduation', emoji: '🎓', label: 'Graduation' },
  { type: 'wedding', emoji: '💒', label: 'Wedding' },
  { type: 'baby', emoji: '👶', label: 'New Baby' },
  { type: 'new_home', emoji: '🏠', label: 'New Home' },
  { type: 'new_job', emoji: '💼', label: 'New Job' },
  { type: 'retirement', emoji: '🎉', label: 'Retirement' },
  { type: 'military', emoji: '🎖️', label: 'Military' },
  { type: 'travel', emoji: '✈️', label: 'Travel' },
  { type: 'custom', emoji: '✨', label: 'Custom' },
];

export const REACTION_TYPES = [
  { type: 'love', emoji: '❤️', label: 'Love' },
  { type: 'laugh', emoji: '😂', label: 'Laugh' },
  { type: 'wow', emoji: '😮', label: 'Wow' },
  { type: 'cry', emoji: '😢', label: 'Cry' },
  { type: 'thank_you', emoji: '🙏', label: 'Thank You' },
  { type: 'learned', emoji: '💡', label: 'Learned' },
  { type: 'treasure', emoji: '💎', label: 'Treasure' },
];

export default {
  getChapters,
  getChapter,
  getChapterTimeline,
  generateChapters,
  updateChapter,
  deleteChapter,
  createChapter,
  getMyLifeData,
  getMyLifeDataForPerson,
  getMilestones,
  getMilestone,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  getMemories,
  getMemory,
  createMemory,
  updateMemory,
  deleteMemory,
  getChapterQuestions,
  answerQuestion,
  addReaction,
  removeReaction,
  getDailyQuestion,
  answerDailyQuestion,
  skipDailyQuestion,
  QUICK_PICK_TYPES,
  REACTION_TYPES,
};
